/**
 * Theme
 * ---
 * Everything that makes an environment *look* like a given brand: logo, palette, fonts, and the
 * few branded images the app references by URL. Themes are independent of environments - several
 * environments (prod / dev / remote of the same brand) share one theme instance, and an
 * environment lists the themes it offers in a single `"themes"` property in its manifest - the
 * first entry is its default, and the user picks between the rest from the header.
 *
 * A theme registers itself into `window.themes` on construction, exactly like an environment
 * registers into `window.environments`. `src/themes/themes.js` imports every theme so that the
 * registry is complete before the active environment is resolved.
 *
 * Anything that is not visual - API urls, OAuth client IDs, payment gates, language packs -
 * stays on the environment.
 */
export class ThemeBase {
	/**
	 * @abstract
	 * Theme ID
	 * ---
	 * Unique identifier of the theme. This is the value environments put in their manifest's
	 * `"themes"` property.
	 * @type {string}
	 */
	static themeID;

	/**
	 * Theme ID
	 * ---
	 * @return {string}
	 * @see ThemeBase.themeID
	 */
	get themeID() {
		return this.constructor.themeID;
	}

	/**
	 * Name language key
	 * ---
	 * Key of the label shown to the user in the theme picker. Themes are brands, so the name is
	 * mostly the brand itself - the key exists so qualifiers like "(legacy)" can be translated.
	 * @return {string}
	 */
	get nameLangKey() {
		return `themeName_${this.themeID}`;
	}

	/**
	 * @abstract
	 * Primary color
	 * ---
	 * The brand's primary color. Used for the PWA manifest's `theme_color`, the `theme-color`
	 * meta tag, the MS tile color and the environment switcher in the menu.
	 * @return {string}
	 */
	get primaryColor() {}

	/**
	 * @abstract
	 * Background color
	 * ---
	 * Color the OS paints around/behind the app while it starts. Ends up in the PWA manifest's
	 * `background_color` and in the MS tile configuration.
	 * @return {string}
	 */
	get backgroundColor() {}

	/**
	 * Style sheet
	 * ---
	 * CSS injected into the document head when the theme is applied. This is where the theme
	 * overrides the variables declared in public/index.css (and, under `html[theme="dark"]`,
	 * the ones in public/index-dark.css - those outrank a plain `:root` block).
	 * @return {string|undefined}
	 */
	get styleSheet() {}

	/**
	 * Font style sheets
	 * ---
	 * URLs of external stylesheets (Google Fonts and the like) the theme needs. Fonts shipped
	 * with the theme belong in its own `assets/font` directory and are declared with `@font-face`
	 * inside `styleSheet` instead.
	 * @return {string[]}
	 */
	get fontStyleSheets() {
		return [];
	}

	/**
	 * Style variables
	 * ---
	 * Style variables applied as inline properties of the `:root` element. They outrank every
	 * stylesheet, so prefer `styleSheet` and keep this for values that have to be computed.
	 * @return {Object<string, string>|undefined}
	 */
	get styleVariables() {}

	/**
	 * Style overrides
	 * ---
	 * Element specific style overrides, keyed by element name.
	 *
	 * Example:
	 * ```
	 *  >>> SomeTheme.js
	 *
	 *  get styleOverrides() {
	 *      return {
	 *              "polyfazer-app": html`<style>background: red</style>`
	 *          }
	 *      }
	 *  }
	 *
	 *  --------------------------------------------------------
	 *  >>> polyfazer-app.js
	 *
	 *  class PolyfazerApp extends PolymerElement {
	 *      static get template() {
	 *          return html`
	 *              <style>
	 *                  background: blue;
	 *              </style>
	 *              ${window.activeEnvironment.styleOverrides["polyfazer-app"]}
	 *          `
	 *      }
	 *  }
	 *
	 *  //And now the background should be red
	 * ```
	 * @return {Object<string, HTMLTemplateElement>}
	 */
	get styleOverrides() {
		return {};
	}

	/**
	 * @abstract
	 * Logo for menu
	 * ---
	 * Logo template for the menu logo.
	 * @return {string}
	 */
	get logoForMenu() {}

	/**
	 * @abstract
	 * Logo for loader
	 * ---
	 * Logo template for the initial page loading spinner.
	 * @return {string}
	 */
	get logoForLoader() {}

	/**
	 * @abstract
	 * Get logo
	 * ---
	 * Logo template for use in various places like a Sygic or an embedded payment page.
	 * @param {string} attributes Attributes rendered onto the root `<svg>` element
	 * @return {string}
	 */
	getLogo(attributes = '') {}

	/**
	 * @abstract
	 * Charging animation url
	 * ---
	 * Url of the animated wave shown for a running session (see session-detail).
	 * @return {string}
	 */
	get chargingAnimationUrl() {}

	/**
	 * Cluster image url
	 * ---
	 * @return {{url: string, size: google.maps.Size}|undefined} undefined = default
	 */
	get clusterIcon() {}

	/**
	 * Cluster label color
	 * ---
	 * @return {string|undefined} undefined = default
	 */
	get clusterLabelColor() {}

	constructor() {
		if(!this.themeID) {
			throw Error(`Property "themeID" must be defined for class ${this.constructor.name}.`);
		}

		if(!window.themes) {
			window.themes = {};
		}

		if(window.themes[this.themeID]) {
			throw Error(`Conflicting theme ID "${this.themeID}" of ${this.constructor.name} with ${window.themes[this.themeID].constructor.name}`);
		}
		window.themes[this.themeID] = this;
	}

	/**
	 * Apply
	 * ---
	 * Applies the theme's fonts and stylesheet to the document. Called by `EnvBase.coreApply()`
	 * before the environment applies its own icons and metadata.
	 */
	apply() {
		this.fontStyleSheets.forEach(href => {
			const link = document.createElement("link");
			link.rel = "stylesheet";
			link.href = href;
			document.head.appendChild(link);
		});

		const styleSheet = this.styleSheet;

		if(styleSheet) {
			const style = document.createElement("style");
			style.textContent = styleSheet;
			document.head.appendChild(style);
		}
	}
}
